import { jsx, jsxs } from "react/jsx-runtime";
import { ArrowDropDown } from "@mui/icons-material";
import { ClickAwayListener, Grow, Paper, Popper, useThemeProps } from "@mui/material";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { useSlotProps } from "../../base/index.js";
import { Chip } from "../Chip/index.js";
import { chipMenuName } from "./classes.js";
import { useChipMenu } from "./components/ChipMenuContextProvider.js";
import { Root } from "./components/Root.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
function noop() {
}
const ChipMenu = forwardRef(function ChipMenu2(inProps, ref) {
  const props = useThemeProps({
    name: chipMenuName,
    props: inProps
  });
  const {
    sx,
    testId = `${chipMenuName}-root`,
    className = "",
    components,
    componentsProps,
    children,
    label,
    openIcon = /* @__PURE__ */ jsx(ArrowDropDown, {}),
    closedIcon = /* @__PURE__ */ jsx(ArrowDropDown, {}),
    disableClickAwayListener = false,
    actions
  } = props;
  const ownerState = props;
  const utilityClasses = useUtilityClasses(ownerState);
  const { open, onToggle, onClose, wrapWithOnClose, onOpen, setOpen } = useChipMenu();
  useImperativeHandle(
    actions,
    () => ({
      onToggle,
      onClose,
      wrapWithOnClose,
      onOpen,
      setOpen
    }),
    [onToggle, onClose, wrapWithOnClose, onOpen, setOpen]
  );
  const anchorRef = useRef(null);
  const rootSlotProps = useSlotProps({
    className: `${className} ${utilityClasses.root}`,
    ownerState,
    elementType: Root,
    additionalProps: {
      sx,
      as: components?.Root,
      ref,
      "data-tidal": chipMenuName,
      "data-testid": testId
    },
    externalSlotProps: componentsProps?.root
  });
  const chipProps = useSlotProps({
    ownerState,
    className: utilityClasses.chip,
    elementType: Chip,
    additionalProps: {
      as: components?.Chip,
      ref: anchorRef,
      onDelete: onToggle,
      onClick: onToggle,
      deleteIcon: open ? openIcon : closedIcon,
      label,
      variant: "outlined"
    },
    externalSlotProps: componentsProps?.chip
  });
  const popperProps = useSlotProps({
    ownerState,
    className: utilityClasses.popper,
    elementType: Popper,
    additionalProps: {
      as: components?.Popper,
      open: !!open,
      anchorEl: anchorRef.current,
      placement: "bottom-start",
      transition: true,
      disablePortal: false
    },
    externalSlotProps: componentsProps?.popper
  });
  const paperProps = useSlotProps({
    ownerState,
    className: utilityClasses.paper,
    elementType: Paper,
    additionalProps: {
      as: components?.Paper,
      sx: {
        mt: 1
      }
    },
    externalSlotProps: componentsProps?.paper
  });
  const growProps = useSlotProps({
    ownerState,
    className: utilityClasses.grow,
    elementType: Grow,
    additionalProps: {
      as: components?.Grow,
      style: {
        transformOrigin: "left top"
      }
    },
    externalSlotProps: componentsProps?.grow
  });
  return /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: disableClickAwayListener ? noop : onClose, children: /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, children: [
    /* @__PURE__ */ jsx(Chip, { ...chipProps }),
    /* @__PURE__ */ jsx(Popper, { ...popperProps, children: ({ TransitionProps }) => /* @__PURE__ */ jsx(Grow, { ...TransitionProps, ...growProps, children: /* @__PURE__ */ jsx(Paper, { ...paperProps, children }) }) })
  ] }) });
});
export {
  ChipMenu
};
//# sourceMappingURL=ChipMenu.js.map
